import React, { useState, useEffect } from 'react';
import { Chart } from 'chart.js';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import { PolicyImpactResults, PolicyScenario } from '../../services/policyImpactService';
import { generatePolicyTimeSeriesData, TimeSeriesEmissionData } from '../../services/policyTimeSeriesService';
import './PolicyImpactChart.css';
import { supabaseClient } from '../../services/supabase';

interface PolicyImpactChartProps {
  results: PolicyImpactResults | null;
  loading: boolean;
  scenario: PolicyScenario;
  location: string;
  chartType?: 'line' | 'contributions' | 'forecast';
}

const PolicyImpactChart: React.FC<PolicyImpactChartProps> = ({ results, loading, scenario, location, chartType = 'line' }) => {
  const [timeSeriesData, setTimeSeriesData] = useState<{
    baselineData: TimeSeriesEmissionData[];
    projectedData: TimeSeriesEmissionData[];
  } | null>(null);
  const [timeSeriesLoading, setTimeSeriesLoading] = useState(false);

  // Forecast datasets (BAU vs With Policies) with wedge areas (VMT, EV)
  const [forecastData, setForecastData] = useState<null | {
    years: number[];
    bauTotals: number[];
    withPoliciesTotals: number[];
    vmtWedge: number[];
    evWedge: number[];
  }>(null);

  // Generate time series data when scenario or location changes
  useEffect(() => {
    const generateTimeSeries = async () => {
      if (!location) return;
      
      setTimeSeriesLoading(true);
      try {
        const data = await generatePolicyTimeSeriesData(location, scenario);
        setTimeSeriesData(data);
      } catch (error) {
        console.error('Error generating time series data:', error);
      } finally {
        setTimeSeriesLoading(false);
      }
    };

    generateTimeSeries();
  }, [location, scenario]);

  // Build forecast data whenever chart type or scenario changes
  useEffect(() => {
    const buildForecast = async () => {
      try {
        // Helper: zero-out all non-transport levers so BAU and isolated cases are unaffected by unrelated policies
        const zeroedPolicies = {
          heatPumpAdoptionPercent: 0,
          solarCapacityMultiplier: 1,
          hdvElectrificationPercent: 0,
          vmtReductionComponents: (scenario as any).vmtReductionComponents || {
            transitOrientedDevelopment: 0,
            activeTransportation: 0,
            transitImprovements: 0,
          },
          parkingPricing: (scenario as any).parkingPricing || { priceIncreasePercent: 0, vmtAffectedPercent: 0, commuteByCarPercent: 0, basePricePerHour: 0 },
          micromobility: (scenario as any).micromobility || { scalePercent: 0, tripsPerResident: 0, percentBike: 0, percentScooter: 0, milesPerBikeTrip: 0, milesPerScooterTrip: 0, percentBikeTripsDisplaceCars: 0, percentScooterTripsDisplaceCars: 0, kwhPerMileBike: 0, kwhPerMileScooter: 0, yearImplemented: 2025 },
        } as Partial<PolicyScenario>;

        // BAU (no VMT reduction, no added EV beyond current, all other policy levers neutral)
        const bauScenario: PolicyScenario = {
          ...scenario,
          ...zeroedPolicies,
          vmtReductionPercent: 0,
          evAdoptionPercent: results?.baseline.currentEVPercent || 0,
        } as PolicyScenario;
        const bau = await generatePolicyTimeSeriesData(location, bauScenario);

        // VMT only
        const vmtOnlyScenario: PolicyScenario = {
          ...scenario,
          ...zeroedPolicies,
          vmtReductionPercent: scenario.vmtReductionPercent || 0,
          evAdoptionPercent: results?.baseline.currentEVPercent || 0,
        } as PolicyScenario;
        const vmtOnly = await generatePolicyTimeSeriesData(location, vmtOnlyScenario);

        // EV only
        const evOnlyScenario: PolicyScenario = {
          ...scenario,
          ...zeroedPolicies,
          vmtReductionPercent: 0,
          evAdoptionPercent: scenario.evAdoptionPercent || (results?.baseline.currentEVPercent || 0),
        } as PolicyScenario;
        const evOnly = await generatePolicyTimeSeriesData(location, evOnlyScenario);

        // With both (current scenario)
        const both = await generatePolicyTimeSeriesData(location, {
          ...scenario,
          heatPumpAdoptionPercent: scenario.heatPumpAdoptionPercent || 0,
          solarCapacityMultiplier: scenario.solarCapacityMultiplier || 1,
          hdvElectrificationPercent: scenario.hdvElectrificationPercent || 0,
        } as PolicyScenario);

        const yrs = bau.projectedData.map(r => r.year);
        const sumCore = (row: TimeSeriesEmissionData) => (row.lightDutyEmissions + row.heavyDutyEmissions + row.naturalGasEmissions + row.electricityEmissions);

        // Compute "Other" baseline (everything not LDV/HDV/NG/Electricity) using current results baseline
        const coreBaseline =
          (results?.baseline.lightDutyGHG || 0) +
          (results?.baseline.heavyDutyGHG || 0) +
          (results?.baseline.naturalGasGHG || 0) +
          (timeSeriesData?.baselineData?.[timeSeriesData.baselineData.length - 1]?.electricityEmissions || 0);
        const otherBaseline = Math.max(0, (results?.baseline.totalGHG || 0) - coreBaseline);

        // Fetch population series and project to requested years (fallback: flat)
        let popByYear: Record<number, number> = {};
        try {
          const { data: popRows } = await supabaseClient
            .from('population')
            .select('year, population')
            .eq('location_name', location)
            .order('year', { ascending: true });
          if (popRows && popRows.length > 0) {
            popRows.forEach((r: any) => { popByYear[r.year] = r.population; });
            // Simple linear extrapolation to 2045 if needed
            const yearsAvail = Object.keys(popByYear).map(y => parseInt(y)).sort((a,b)=>a-b);
            const lastYear = yearsAvail[yearsAvail.length - 1];
            const prevYear = yearsAvail[yearsAvail.length - 2] || lastYear - 1;
            const growth = lastYear > prevYear ? (popByYear[lastYear] - popByYear[prevYear]) / (lastYear - prevYear) : 0;
            const neededYears = yrs.filter(y => !(y in popByYear));
            neededYears.forEach(y => { popByYear[y] = popByYear[lastYear] + growth * (y - lastYear); });
          }
        } catch (e) {
          console.warn('Population lookup failed; using flat other emissions', e);
        }
        const basePop = popByYear[2023] || popByYear[Object.keys(popByYear).map(n=>parseInt(n)).sort((a,b)=>b-a)[0]] || 1;
        const otherByYear = yrs.map(y => Math.round(otherBaseline * (popByYear[y] ? popByYear[y] / basePop : 1)));

        const sumTotals = (rows: TimeSeriesEmissionData[]) => rows.map((r, i) => Math.round(sumCore(r) + (otherByYear[i] || 0)));

        const bauTotals = sumTotals([...bau.baselineData, ...bau.projectedData].slice(bau.baselineData.length));
        const vmtOnlyTotals = sumTotals([...vmtOnly.baselineData, ...vmtOnly.projectedData].slice(vmtOnly.baselineData.length));
        const evOnlyTotals = sumTotals([...evOnly.baselineData, ...evOnly.projectedData].slice(evOnly.baselineData.length));
        const bothTotals = sumTotals([...both.baselineData, ...both.projectedData].slice(both.baselineData.length));

        // Construct wedges for forecast horizon
        const vmtWedge = bauTotals.map((v, i) => Math.max(0, v - vmtOnlyTotals[i]));
        const evWedge = vmtOnlyTotals.map((v, i) => Math.max(0, v - bothTotals[i]));

        setForecastData({ years: yrs, bauTotals, withPoliciesTotals: bothTotals, vmtWedge, evWedge });
      } catch (e) {
        console.warn('Failed to build forecast data', e);
        setForecastData(null);
      }
    };
    if (chartType === 'forecast') buildForecast();
  }, [chartType, location, JSON.stringify(scenario)]);

  if (loading || timeSeriesLoading) {
    return (
      <div className="policy-impact-loading">
        <div className="loading-spinner"></div>
        <p>Calculating policy impacts...</p>
      </div>
    );
  }

  if (!results || !timeSeriesData) {
    return (
      <div className="policy-impact-placeholder">
        <p>Adjust policy levers to see impact on GHG emissions</p>
      </div>
    );
  }

  // Prepare data for time series chart
  const years = timeSeriesData.baselineData.map(d => d.year);
  const startYear = Math.min(...years);
  const endYear = Math.max(...years);
  const baselineData = {
    lightDuty: timeSeriesData.baselineData.map(d => d.lightDutyEmissions),
    heavyDuty: timeSeriesData.baselineData.map(d => d.heavyDutyEmissions),
    naturalGas: timeSeriesData.baselineData.map(d => d.naturalGasEmissions),
    electricity: timeSeriesData.baselineData.map(d => d.electricityEmissions)
  };
  const projectedData = {
    lightDuty: timeSeriesData.projectedData.map(d => d.lightDutyEmissions),
    heavyDuty: timeSeriesData.projectedData.map(d => d.heavyDutyEmissions),
    naturalGas: timeSeriesData.projectedData.map(d => d.naturalGasEmissions),
    electricity: timeSeriesData.projectedData.map(d => d.electricityEmissions)
  };

  

  const chartData = {
    labels: years,
    datasets: [
      {
        label: 'Light Duty Vehicles (Baseline)',
        data: baselineData.lightDuty,
        backgroundColor: 'rgba(59, 130, 246, 0.3)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Light Duty Vehicles (With Policies)',
        data: projectedData.lightDuty,
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 3,
        borderDash: [5, 5],
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Natural Gas (Baseline)',
        data: baselineData.naturalGas,
        backgroundColor: 'rgba(16, 185, 129, 0.3)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Natural Gas (With Policies)',
        data: projectedData.naturalGas,
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 3,
        borderDash: [5, 5],
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Electricity (Baseline)',
        data: baselineData.electricity,
        backgroundColor: 'rgba(245, 158, 11, 0.3)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Electricity (With Policies)',
        data: projectedData.electricity,
        backgroundColor: 'rgba(245, 158, 11, 0.8)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 3,
        borderDash: [5, 5],
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
    ],
  };

  // Build forecast chart data/options when available
  const forecastChartData = forecastData ? (() => {
    const bau = forecastData.bauTotals;
    const afterVmt = forecastData.bauTotals.map((v, i) => v - forecastData.vmtWedge[i]);
    const afterEv = afterVmt.map((v, i) => v - forecastData.evWedge[i]);

    return {
      labels: forecastData.years,
      datasets: [
        // BAU line (top reference)
        {
          label: 'Total GHG (BAU)',
          data: bau,
          borderColor: 'rgba(31, 41, 55, 1)',
          backgroundColor: 'rgba(31, 41, 55, 0.1)',
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0.2,
          order: 3,
        },
        // VMT wedge: area between BAU and After-VMT
        {
          label: 'VMT Reduction',
          data: afterVmt,
          borderColor: 'rgba(59, 130, 246, 0)',
          backgroundColor: 'rgba(59, 130, 246, 0.35)',
          pointRadius: 0,
          fill: {
            target: '-1', // fill to previous dataset (BAU)
          },
          tension: 0.2,
          order: 2,
        },
        // EV wedge: area between After-VMT and After-EV (With Policies)
        {
          label: 'Vehicle Electrification',
          data: afterEv,
          borderColor: 'rgba(96, 165, 250, 0)',
          backgroundColor: 'rgba(96, 165, 250, 0.35)',
          pointRadius: 0,
          fill: {
            target: '-1', // fill to previous dataset (After-VMT)
          },
          tension: 0.2,
          order: 1,
        },
        // With policies line (remaining emissions)
        {
          label: 'Total GHG (With Policies)',
          data: afterEv,
          borderColor: 'rgba(34, 197, 94, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
          tension: 0.2,
          order: 4,
        },
      ]
    };
  })() : null;

  const forecastChartOptions = {
    type: 'line' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const },
      tooltip: { intersect: false, mode: 'index' as const },
    },
    scales: {
      y: { title: { display: true, text: 'Emissions (Metric Tons CO₂e)' } },
      x: { title: { display: true, text: 'Year' } }
    }
  };

  const chartOptions = {
    type: 'line' as const,
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: false,
          padding: 15,
          generateLabels: function(chart: any) {
            const original = Chart.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            
            labels.forEach((label: any, index: number) => {
              const dataset = chart.data.datasets[index];
              if (dataset.label && dataset.label.includes('With Policies')) {
                // Add custom rendering for dotted lines in legend
                label.lineDash = dataset.borderDash || [0, 0];
              }
            });
            
            return labels;
          }
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${value.toLocaleString()} MT CO₂e`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year',
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Emissions (Metric Tons CO₂e)',
        },
        ticks: {
          callback: (value: any) => Number(value).toLocaleString(),
        },
      },
    },
  };

  // Calculate total reduction for percentages
  const totalReduction = results.reductions.totalReduction;

  // Check if we have VMT component breakdown
  const hasVmtBreakdown = results.contributionsByPolicy.vmtReductionByComponent;
  const vmtComponents = results.contributionsByPolicy.vmtReductionByComponent;

  // Create labels with policy values and percentages
  const contributions = hasVmtBreakdown && vmtComponents
    ? [
        {
          name: 'Vehicle Electrification',
          value: Math.round(results.contributionsByPolicy.evAdoption),
          setting: `${scenario.evAdoptionPercent}%`,
        },
        {
          name: 'VMT: Transit-Oriented Dev',
          value: Math.round(vmtComponents.transitOrientedDevelopment),
          setting: `${scenario.vmtReductionComponents.transitOrientedDevelopment.toFixed(1)}%`,
        },
        {
          name: 'VMT: Active Transportation',
          value: Math.round(vmtComponents.activeTransportation),
          setting: `${scenario.vmtReductionComponents.activeTransportation.toFixed(1)}%`,
        },
        {
          name: 'VMT: Telework Programs',
          value: Math.round(vmtComponents.teleworkPrograms),
          setting: `${scenario.vmtReductionComponents.teleworkPrograms.toFixed(1)}%`,
        },
        {
          name: 'VMT: Transit Improvements',
          value: Math.round(vmtComponents.transitImprovements),
          setting: `${scenario.vmtReductionComponents.transitImprovements.toFixed(1)}%`,
        },
        {
          name: 'Parking Pricing',
          value: Math.round(results.contributionsByPolicy.parkingPricing),
          setting: `${scenario.parkingPricing.priceIncreasePercent}% price increase`,
        },
        {
          name: 'Expand Micromobility',
          value: Math.round(results.contributionsByPolicy.micromobility || 0),
          setting: `${scenario.micromobility?.scalePercent || 0}% scale`,
        },
        {
          name: 'Heat Pumps',
          value: Math.round(results.contributionsByPolicy.heatPumpAdoption),
          setting: `${scenario.heatPumpAdoptionPercent}%`,
        },
        {
          name: 'Solar Capacity',
          value: Math.round(results.contributionsByPolicy.solarCapacity),
          setting: `${scenario.solarCapacityMultiplier}x`,
        },
        {
          name: 'HDV Electrification',
          value: Math.round(results.contributionsByPolicy.hdvElectrification),
          setting: `${scenario.hdvElectrificationPercent}%`,
        },
      ]
    : [
        {
          name: 'Vehicle Electrification',
          value: Math.round(results.contributionsByPolicy.evAdoption),
          setting: `${scenario.evAdoptionPercent}%`,
        },
        {
          name: 'VMT Reduction',
          value: Math.round(results.contributionsByPolicy.vmtReduction),
          setting: `${scenario.vmtReductionPercent}%`,
        },
        {
          name: 'Parking Pricing',
          value: Math.round(results.contributionsByPolicy.parkingPricing),
          setting: `${scenario.parkingPricing.priceIncreasePercent}% price increase`,
        },
        {
          name: 'Expand Micromobility',
          value: Math.round(results.contributionsByPolicy.micromobility || 0),
          setting: `${scenario.micromobility?.scalePercent || 0}% scale`,
        },
        {
          name: 'Heat Pumps',
          value: Math.round(results.contributionsByPolicy.heatPumpAdoption),
          setting: `${scenario.heatPumpAdoptionPercent}%`,
        },
        {
          name: 'Solar Capacity',
          value: Math.round(results.contributionsByPolicy.solarCapacity),
          setting: `${scenario.solarCapacityMultiplier}x`,
        },
        {
          name: 'HDV Electrification',
          value: Math.round(results.contributionsByPolicy.hdvElectrification),
          setting: `${scenario.hdvElectrificationPercent}%`,
        },
      ];

  // Prepare data for waterfall chart showing contributions
  const truncate = (text: string, max: number = 28) =>
    text.length > max ? `${text.slice(0, max - 1)}…` : text;
  const fmtTons = (v: number) => (v >= 1000 ? `${Math.round(v/100)/10}k` : `${Math.round(v)}`);

  const contributionsData = {
    labels: contributions.map((c) => truncate(`${c.name} (${c.setting}, ${fmtTons(c.value)} t)`)),
    datasets: [
      {
        label: 'GHG Reduction (MT CO₂e)',
        data: contributions.map((c) => c.value),
        backgroundColor: hasVmtBreakdown
          ? [
              'rgba(59, 130, 246, 0.8)', // EV Adoption - Blue
              'rgba(5, 150, 105, 0.8)', // VMT: TOD - Green-600
              'rgba(16, 185, 129, 0.8)', // VMT: Active Transport - Green-500
              'rgba(52, 211, 153, 0.8)', // VMT: Telework - Green-400
              'rgba(110, 231, 183, 0.8)', // VMT: Transit - Green-300
              'rgba(34, 197, 94, 0.8)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 0.8)', // Micromobility - Green-700
              'rgba(245, 158, 11, 0.8)', // Heat Pumps - Orange
              'rgba(139, 92, 246, 0.8)', // Solar - Purple
              'rgba(236, 72, 153, 0.8)', // HDV - Pink
            ]
          : [
              'rgba(59, 130, 246, 0.8)',
              'rgba(16, 185, 129, 0.8)',
              'rgba(34, 197, 94, 0.8)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 0.8)', // Micromobility - Green-700
              'rgba(245, 158, 11, 0.8)',
              'rgba(139, 92, 246, 0.8)',
              'rgba(236, 72, 153, 0.8)',
            ],
        borderColor: hasVmtBreakdown
          ? [
              'rgba(59, 130, 246, 1)',
              'rgba(5, 150, 105, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(52, 211, 153, 1)',
              'rgba(110, 231, 183, 1)',
              'rgba(34, 197, 94, 1)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 1)', // Micromobility - Green-700
              'rgba(245, 158, 11, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(236, 72, 153, 1)',
            ]
          : [
              'rgba(59, 130, 246, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(34, 197, 94, 1)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 1)', // Micromobility - Green-700
              'rgba(245, 158, 11, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(236, 72, 153, 1)',
            ],
        borderWidth: 2,
      },
    ],
  };

  const contributionsOptions = {
    type: 'bar' as const,
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.x;
            const percentage = totalReduction > 0 ? ((value / totalReduction) * 100).toFixed(1) : '0';
            return [
              `Reduction: ${value.toLocaleString()} MT CO₂e`,
              `Contribution: ${percentage}% of total`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'GHG Reduction (Metric Tons CO₂e)',
        },
        ticks: {
          callback: (value: any) => Number(value).toLocaleString(),
        },
      },
      y: {
        ticks: {
          autoSkip: false,
          padding: 6,
        },
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <div className="policy-impact-chart-container">
      {chartType === 'line' ? (
        /* Line Chart */
      <div className="chart-section">
        <ChartRendererSupabase
          chartData={chartData}
          chartOptions={chartOptions}
            height="500px"
        />
      </div>
      ) : chartType === 'contributions' ? (
        /* Contributions Chart */
      <div className="chart-section">
        <ChartRendererSupabase
          chartData={contributionsData}
          chartOptions={contributionsOptions}
            height="500px"
          />
        </div>
      ) : (
        /* Forecast Chart */
        <div className="chart-section">
          <ChartRendererSupabase
            chartData={forecastChartData || { labels: [], datasets: [] }}
            chartOptions={forecastChartOptions}
            height="500px"
        />
      </div>
      )}
    </div>
  );
};

export default PolicyImpactChart;
